<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[10060] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      @keydown.escape.window="onEscape"
    >
      <div class="cliente-submodal bg-white dark:bg-gray-800 shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh]">
        <header class="cliente-submodal__header">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md shrink-0">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-bold text-gray-900 dark:text-white">
              {{ isEdit ? t('workspaceClienteModal.endereco.editTitle') : t('workspaceClienteModal.endereco.addTitle') }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('workspaceClienteModal.endereco.formSubtitle') }}</p>
          </div>
        </header>

        <div class="cliente-submodal__body space-y-3 overflow-y-auto">
          <div>
            <span class="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{{ t('workspaceClienteModal.endereco.tipo') }}</span>
            <div class="flex flex-wrap gap-2 mt-2">
              <button
                v-for="opt in tipoOptions"
                :key="opt.value"
                type="button"
                class="px-3 py-1.5 rounded-lg text-sm font-medium border transition-all"
                :class="local.tipo === opt.value
                  ? 'bg-sky-500 text-white border-sky-500 shadow-sm'
                  : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'"
                @click="local.tipo = opt.value"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>

          <ClienteFormField
            v-model="local.cep"
            :label="t('workspaceClienteModal.endereco.cep')"
            required
            :error="fieldErrors.cep"
            :hint="cepLoading ? t('workspaceClienteModal.endereco.cepLoading') : ''"
            inputmode="numeric"
            @update:model-value="onCepInput"
            @blur="onCepBlur"
          >
            <template #icon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            </template>
          </ClienteFormField>

          <ClienteFormField
            v-model="local.logradouro"
            :label="t('workspaceClienteModal.endereco.logradouro')"
            required
            :error="fieldErrors.logradouro"
            @blur="validateLogradouro"
            @update:model-value="onLogradouroInput"
          />

          <div class="grid grid-cols-2 gap-3">
            <ClienteFormField v-model="local.numero" :label="t('workspaceClienteModal.endereco.numero')" />
            <ClienteFormField v-model="local.complemento" :label="t('workspaceClienteModal.endereco.complemento')" />
          </div>

          <ClienteFormField v-model="local.bairro" :label="t('workspaceClienteModal.endereco.bairro')" />

          <div class="grid grid-cols-2 gap-3">
            <ClienteFormField v-model="local.cidade" :label="t('workspaceClienteModal.endereco.cidade')" />
            <ClienteFormField
              v-model="local.estado"
              :label="t('workspaceClienteModal.endereco.estado')"
              :error="fieldErrors.estado"
              @update:model-value="local.estado = ($event || '').toUpperCase().slice(0, 2)"
            />
          </div>

          <label class="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <input v-model="local.principal" type="checkbox" class="rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
            <span class="text-sm font-medium text-gray-800 dark:text-gray-200">{{ t('workspaceClienteModal.endereco.principal') }}</span>
          </label>
        </div>

        <footer class="cliente-submodal__footer">
          <button type="button" class="btn-secondary" @click="$emit('close')">{{ t('common.cancel') }}</button>
          <button type="button" class="btn-primary" :disabled="!canSave" @click="save">{{ t('common.save') }}</button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ClienteFormField from './ClienteFormField.vue'
import { useCepLookup, maskCep } from '@/composables/useCepLookup'
import '@/assets/styles/cliente-modal-theme.css'

const props = defineProps({
  open: { type: Boolean, default: false },
  initial: { type: Object, default: null }
})

const emit = defineEmits(['close', 'save'])

const onEscape = (event) => {
  event.stopPropagation()
  emit('close')
}

const { t } = useI18n()
const { loading: cepLoading, error: cepError, fetchByCep } = useCepLookup()

const empty = () => ({
  tipo: 'RESIDENCIAL',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  principal: false
})

const local = ref(empty())
const fieldErrors = ref({ cep: '', logradouro: '', estado: '' })

const isEdit = computed(() => props.initial != null)

const tipoOptions = computed(() => [
  { value: 'RESIDENCIAL', label: t('workspaceClienteModal.endereco.tipoResidencial') },
  { value: 'COMERCIAL', label: t('workspaceClienteModal.endereco.tipoComercial') },
  { value: 'OUTRO', label: t('workspaceClienteModal.endereco.tipoOutro') }
])

const countLetters = (value) => {
  const matches = String(value || '').match(/\p{L}/gu)
  return matches ? matches.length : 0
}

const isLogradouroValid = (value) => countLetters(value) >= 3

const validateLogradouro = () => {
  fieldErrors.value.logradouro = isLogradouroValid(local.value.logradouro)
    ? ''
    : t('workspaceClienteModal.endereco.validation.logradouro')
}

const onLogradouroInput = () => {
  if (isLogradouroValid(local.value.logradouro)) {
    fieldErrors.value.logradouro = ''
  }
}

const validate = () => {
  const cepDigits = String(local.value.cep || '').replace(/\D/g, '')
  fieldErrors.value.cep = cepDigits.length === 8 ? '' : t('workspaceClienteModal.endereco.validation.cep')
  validateLogradouro()
  const uf = (local.value.estado || '').trim()
  fieldErrors.value.estado = !uf || uf.length === 2 ? '' : t('workspaceClienteModal.endereco.validation.estado')
  return !fieldErrors.value.cep && !fieldErrors.value.logradouro && !fieldErrors.value.estado
}

const canSave = computed(() => {
  const cepDigits = String(local.value.cep || '').replace(/\D/g, '')
  return cepDigits.length === 8
    && isLogradouroValid(local.value.logradouro)
    && (local.value.estado || '').trim().length === 2
})

const onCepInput = (v) => {
  local.value.cep = maskCep(v)
}

const onCepBlur = async () => {
  if (!validate() && !local.value.cep) return
  const cepDigits = String(local.value.cep || '').replace(/\D/g, '')
  if (cepDigits.length !== 8) return
  const data = await fetchByCep(cepDigits)
  if (data) {
    if (data.logradouro) local.value.logradouro = data.logradouro
    if (data.bairro) local.value.bairro = data.bairro
    if (data.cidade) local.value.cidade = data.cidade
    if (data.estado) local.value.estado = data.estado
    fieldErrors.value.cep = ''
  } else if (cepError.value === 'notFound') {
    fieldErrors.value.cep = t('workspaceClienteModal.endereco.validation.cepNotFound')
  }
}

watch(() => props.open, (v) => {
  if (v) {
    local.value = props.initial ? { ...empty(), ...props.initial, cep: maskCep(props.initial.cep || '') } : empty()
    fieldErrors.value = { cep: '', logradouro: '', estado: '' }
  }
})

const save = () => {
  if (!validate()) return
  emit('save', { ...local.value, cep: local.value.cep?.replace(/\D/g, '') })
  emit('close')
}
</script>

<style scoped>
.cliente-submodal {
  border-radius: 4px;
  overflow: hidden;
}
.cliente-submodal__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-primary);
}
.cliente-submodal__body {
  padding: 1rem 1.25rem;
  flex: 1;
  min-height: 0;
}
.cliente-submodal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
}
</style>
